import React, { useState } from "react";
import AccordionTable from "../../../../components/accordion/AccordionTable";
import { Box, Grid, MenuItem, Tooltip, Typography } from "@mui/material";

import { InputValueProjectForm } from "./domain";
import { ButtonPrimary } from "../../../../components/button/ButtonCustoms";
import ModalCustom from "../../../../components/modal/ModalCustom";
import { useTranslation } from "react-i18next";
import InputTextField from "../../../../components/input/inputTextField";
import FilterSelect from "../../../../components/select/filterSelect";
import {
  alertActive,
  alertSuccess,
} from "../../../../components/alert/swal";
import { UNIT_MEASUREMENT_OF_INDICATORS } from "../enum";

interface IProjectFormProps {
  inputValue: InputValueProjectForm;
  setInputValue: React.Dispatch<React.SetStateAction<InputValueProjectForm>>;
  disabled?: boolean
}

const IndicatorForm: React.FC<IProjectFormProps> = ({
  inputValue,
  setInputValue,
  disabled = false
}) => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState<boolean>(false);

  const [inputValueIndicator, setInputValueIndicator] = useState<any>({
    indicator_id: "",
    indicator_name: "",
    indicator_type: "",
    indicator_type_name: "",
    indicator_value_1: "",
    indicator_value_2: "",
  });

  const [mode, setMode] = useState<string>("ADD");

  const fileHeaders = [
    {
      key: "index",
      label: t("ลำดับ"),
      width: 1,
      align: "center" as const,
      render: (value: any, rowData: any, rowIndex: number) => (
        <Typography variant="body2">{rowIndex + 1}</Typography>
      ),
    },
    {
      key: "name",
      label: t("ชื่อตัวชี้วัด"),
      width: 4,
      render: (value: string, rowData: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={value}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: "value",
      label: t("ค่าเป้าหมาย"),
      width: 5,
      align: "center" as const,
    },
  ];

  const handleAddIndicator = () => {
    const newIndicator = {
      id: inputValueIndicator.indicator_id ?? new Date().getTime(),
      name: inputValueIndicator.indicator_name,
      type: inputValueIndicator.indicator_type,
      value: inputValueIndicator.indicator_value_1,
      value_2: inputValueIndicator.indicator_value_2,
    };

    setInputValue((prev) => ({
      ...prev,
      indicatorList: [...prev.indicatorList, newIndicator],
    }));

    alertSuccess(t("เพี่มตัวชี้วัด เรียบร้อย"), undefined, undefined, () => {
      setShowModal(false);
      setInputValueIndicator({
        indicator_id: "",
        indicator_name: "",
        indicator_type: "",
        indicator_type_name: "",
        indicator_value_1: "",
        indicator_value_2: "",
      })
    });

    // alertError("เพี่มตัวชี้วัด ไม่สำเร็จ");
  };

  const handleEditIndicator = (
    id: number,
    updatedData: Partial<{ name: string; value: string }>
  ) => {
    setInputValue((prev) => ({
      ...prev,
      indicatorList: prev.indicatorList.map((item) =>
        item.id === id ? { ...item, ...updatedData } : item
      ),
    }));
    // setShowModal(false);
  };

  const handleDeleteIndicator = (_: any, index: number) => {
    alertActive(
      t("ลบตัวชี้วัด"),
      t("ท่านต้องการลบตัวชี้วัด ใช่หรือไม่"),
      undefined,
      undefined,
      async () => {
        setInputValue((prev) => ({
          ...prev,
          indicatorList: prev.indicatorList.filter((_, i) => i !== index),
        }));
        setShowModal(false);
      }
    );
  };

  const handleInputChange = (key: any, value: any) => {
    setInputValueIndicator((prev: any) => ({ ...prev, [key]: value }));
  };

  const indicatorTypeOption = [
    {
      name: t("หน่วยวัดระดับ"),
      value: UNIT_MEASUREMENT_OF_INDICATORS.LEVEL,
    },
    {
      name: t("หน่วยวัดจำนวน"),
      value: UNIT_MEASUREMENT_OF_INDICATORS.QUANTITY,
    },
    {
      name: t("หน่วยวัดคะแนน"),
      value: UNIT_MEASUREMENT_OF_INDICATORS.SCORE,
    },
    {
      name: t("หน่วยวัดเปอร์เซ็นต์"),
      value: UNIT_MEASUREMENT_OF_INDICATORS.PERCENT,
    },
    {
      name: t("หน่วยวัดร้อยละ"),
      value: UNIT_MEASUREMENT_OF_INDICATORS.RATIO_PERCENT,
    },
    {
      name: t("หน่วยวัดสัดส่วน"),
      value: UNIT_MEASUREMENT_OF_INDICATORS.PROPORTION,
    },
  ];

  return (
    <>
      {showModal && (
        <ModalCustom
          open
          title={t("เพิ่มตัวชี้วัด")}
          subTitle={t("")}
          onClose={() => {
            setShowModal(false);
            setMode("");
            setInputValueIndicator({
              indicator_id: "",
              indicator_name: "",
              indicator_type: "",
              indicator_type_name: "",
              indicator_value_1: "",
              indicator_value_2: "",
            })
          }}
          component={
            <div className="w-full">
              <section className="w-full mt-10">
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    sx={{
                      alignSelf: "end",
                    }}
                  >
                    <InputTextField
                      heading={t("ชื่อตัวชี้วัด")}
                      placeholder={t("ชื่อตัวชี้วัด")}
                      value={inputValueIndicator.indicator_name ?? ""}
                      onchange={(e: any) =>
                        handleInputChange("indicator_name", e.target.value)
                      }
                      required
                      helperText={""}
                      disabled={mode == 'EDIT'}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    sx={{
                      alignSelf: "end",
                    }}
                  >
                    <FilterSelect
                      value={inputValueIndicator.indicator_type ?? ""}
                      labelId="ประเภทค่าเป้าหมาย-id"
                      selectId="ประเภทค่าเป้าหมาย-select-id"
                      heading={t("ประเภทค่าเป้าหมาย")}
                      disabled={mode == 'EDIT'}
                      options={[...indicatorTypeOption].map(
                        (item: any, index: number) => (
                          <MenuItem value={item?.value} key={index + 1}>
                            {item?.name}
                          </MenuItem>
                        )
                      )}
                      onchange={(e: any) => {
                        const selectedValue = e.target.value;
                        const selectedItem = indicatorTypeOption.find(item => item?.value === selectedValue);

                        handleInputChange("indicator_type", selectedValue);
                        handleInputChange("indicator_type_name", selectedItem?.name || "");
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    sx={{
                      alignSelf: "end",
                    }}
                  >
                    <Typography variant="body1" gutterBottom>
                      {t("ระบุค่าเป้าหมาย")}
                    </Typography>
                  </Grid>

                  {inputValueIndicator.indicator_type && (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={3}
                        lg={3}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <Typography variant="body1" gutterBottom>
                          {inputValueIndicator.indicator_type_name}
                        </Typography>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        lg={3}
                        sx={{ alignSelf: "end" }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <InputTextField
                            placeholder={t("ระบุ")}
                            value={inputValueIndicator.indicator_value_1 ?? ""}
                            onchange={(e: any) =>
                              handleInputChange(
                                "indicator_value_1",
                                e.target.value
                              )
                            }
                            required
                            helperText=""
                            disabled={disabled || mode == 'EDIT'}
                          />

                          {inputValueIndicator.indicator_type ===
                            UNIT_MEASUREMENT_OF_INDICATORS.PROPORTION && (
                              <>
                                <Typography variant="body1">/</Typography>
                                <InputTextField
                                  placeholder={t("ระบุ")}
                                  value={
                                    inputValueIndicator.indicator_value_2 ?? ""
                                  }
                                  onchange={(e: any) =>
                                    handleInputChange(
                                      "indicator_value_2",
                                      e.target.value
                                    )
                                  }
                                  required
                                  helperText=""
                                  disabled={mode == 'EDIT'}
                                />
                              </>
                            )}
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        lg={3}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <Typography variant="body1" gutterBottom>
                          {inputValueIndicator.indicator_type ===
                            UNIT_MEASUREMENT_OF_INDICATORS.SCORE
                            ? t("คะแนน")
                            : inputValueIndicator.indicator_type ===
                              UNIT_MEASUREMENT_OF_INDICATORS.PERCENT
                              ? "%"
                              : ""}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </section>

              <div className="mt-6 w-full flex justify-center gap-4 items-center">
                <span>
                  <ButtonPrimary
                    mode="cancel"
                    buttonName={t("BUTTON.CANCEL")}
                    onClick={() => {
                      setShowModal(false);
                    }}
                  />
                </span>
                {mode !== 'EDIT' && (
                  <span>
                    <ButtonPrimary
                      mode="next"
                      disabled={!inputValueIndicator.indicator_name}
                      buttonName={t("ยืนยัน")}
                      onClick={() => {
                        handleAddIndicator();
                      }}
                    />
                  </span>
                )}
              </div>
            </div>
          }
        />
      )}

      <AccordionTable
        defaultExpanded
        required
        titleName={t("ตัวชี้วัด")}
        headers={fileHeaders}
        data={inputValue.indicatorList}
        enableButtonLeft
        onClickButtonLeft={() => {
          setMode("ADD");
          setShowModal(true);
        }}
        onViewClick={(rowData) => {
          setMode("EDIT");
          setShowModal(true);
          setInputValueIndicator({
            indicator_id: "",
            indicator_name: rowData?.name || "",
            indicator_type: rowData?.type || "",
            indicator_type_name: "",
            indicator_value_1: rowData?.value || "",
            indicator_value_2: rowData?.value_2 || "",
          })
          // handleEditIndicator(rowData.id, { name: rowData.name, value: rowData.value });
        }}
        onDeleteClick={handleDeleteIndicator}
        isView={disabled}
      />
    </>
  );
};

export default IndicatorForm;
